import { useState, useCallback } from 'react';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconImageOutline } from '@picsart/design-system/foundation/icons/IconImageOutline';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { useClickOutsideRef } from '@picsart/design-system/hooks/dom';
import { useDeviceType } from '@picsart/design-system/hooks/device';
import UserMenuButton from '@/components/UserMenuButton';
import SettingsPanel from '@/components/SettingsPanel';
import { SettingsPanelVariant } from '@/components/SettingsPanel/types';
import useUserProfile from '@/hooks/useUserProfile';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import useStyles from './styles';
import type { HeaderRightProps } from './types';

const HeaderRight = ({
  showGenerationsButton,
  onGenerationsClick,
  onCloseApp,
  onSettingsClick,
  onAvatarClick,
}: HeaderRightProps) => {
  const classes = useStyles();
  const isPortfolioApp = checkIsPortfolioApp();
  const {
    isAuthorized: userIsAuthorized,
    username,
    avatarSrc,
    tierId,
  } = useUserProfile();
  const { isMobile } = useDeviceType();
  const [showDesktopSettings, setShowDesktopSettings] = useState(false);
  const closeSettings = useCallback(() => setShowDesktopSettings(false), []);
  const settingsRef = useClickOutsideRef(closeSettings, [], false);

  const handleSettingsClick = () => {
    if (isMobile) {
      onSettingsClick?.();
    } else {
      setShowDesktopSettings((v) => !v);
    }
  };

  return (
    <div className={classes.right}>
      {/* Generations button - CSS hides on desktop; hidden entirely on the
          portfolio surface (creations are reachable from the profile screen) */}
      {showGenerationsButton && !isPortfolioApp && (
        <span className={classes.generationsButton}>
          <InlineButton
            size="large"
            centerIcon={IconImageOutline}
            className={classes.iconButton}
            isWithoutBorder
            isWithoutBackground
            onClick={onGenerationsClick}
            aria-label="Your Creations"
            title="Your Creations"
            dataTestId="header-right-generations"
          />
        </span>
      )}
      {isPortfolioApp ? (
        <div className={classes.settingsWrapper}>
          <UserMenuButton
            isAuthorized={userIsAuthorized}
            username={username}
            avatarSrc={avatarSrc}
            tierId={tierId}
            showUserMenu
            onCloseApp={onCloseApp}
            onSettingsClick={handleSettingsClick}
            onAvatarClick={onAvatarClick}
          />
          {showDesktopSettings && !isMobile && (
            <div ref={settingsRef}>
              <SettingsPanel
                variant={SettingsPanelVariant.Dropdown}
                isAuthorized={userIsAuthorized}
              />
            </div>
          )}
        </div>
      ) : (
        <InlineButton
          size="large"
          centerIcon={IconCross}
          className={classes.iconButton}
          isWithoutBorder
          isWithoutBackground
          onClick={onCloseApp}
          aria-label="Close app"
          title="Close"
          dataTestId="header-right-close"
        />
      )}
    </div>
  );
};

export default HeaderRight;
